<script setup>
/* eslint-disable no-use-before-define, vue/no-bare-strings-in-template, vue/html-closing-bracket-newline, @intlify/vue-i18n/no-raw-text, prettier/prettier */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import BankOffers from './BankOffers.vue';
import BankLogo from '../settings/integrations/BankLogo.vue';
import BureauQueryDialog from './BureauQueryDialog.vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  findContact,
  isValidCpf,
  isValidMobile,
  registerBankContact,
} from './connectedBankContact';
import {
  automaticActionKey,
  canContinueAutomatically,
  journeySummary,
} from './connectedBankJourney';
import TalkviaConnectedBankQueriesAPI from 'dashboard/api/talkviaConnectedBankQueries';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  brazilianDateToIso,
  currencyInputToDecimal,
  formatBrazilianDate,
  formatCpf,
  formatCurrencyInput,
  onlyDigits,
} from 'shared/helpers/talkviaInputMasks';

const props = defineProps({
  embedded: { type: Boolean, default: false },
  initialContact: { type: Object, default: null },
  conversationId: { type: [Number, String], default: null },
});
const emit = defineEmits(['batchChange', 'busyChange']);
let disposed = false;

const router = useRouter();
const { accountScopedRoute } = useAccount();
const cpf = ref('');
const selectedProduct = ref('CLT');
const selectedHookIds = ref([]);
const customer = ref({
  name: '',
  phone: '',
  birth_date: '',
  email: '',
  amount: '',
  installment: '',
});
const selectedContactId = ref(null);
const batches = ref([]);
const providers = ref([]);
const selectedBatch = ref(null);
const selectedLog = ref(null);
const showTechnicalDetails = ref(false);
const pendingDeleteBatch = ref(null);
const pendingDeleteCpfGroup = ref(null);
const actionResult = ref(null);
const actionForm = ref({});
const deleteDialogRef = ref(null);
const deleteCpfDialogRef = ref(null);
const feedbackDialogRef = ref(null);
const bureauDialogRef = ref(null);
const feedback = ref({ title: '', message: '' });
const isLoading = ref(false);
const isCreating = ref(false);
const deletingBatchId = ref(null);
const deletingCpfKey = ref(null);
const isSubmittingAction = ref(false);
const retryingResultId = ref(null);
const copyingAuthorizationResultId = ref(null);
const contactLookupMessage = ref('');
const contactLookupStatus = ref('idle');
const showCpf = ref(false);
const visibleRecentCpfKeys = ref(new Set());
const compactRecentCpfKeys = ref(new Set());
const isBankSelectionExpanded = ref(true);
const isCustomerDetailsExpanded = ref(false);
let pollingTimer = null;
let contactLookupTimer = null;
let contactLookupSequence = 0;
let autoFilledCustomer = {};
const automaticContinuationAttempts = new Set();
const queryStatusAttempts = new Map();
const continuationErrors = ref({});
const isContinuingAutomatically = ref(false);
const journey = computed(() =>
  journeySummary(selectedBatch.value?.results || [])
);
const sharedAuthorizationResults = computed(() =>
  (selectedBatch.value?.results || []).filter(
    result =>
      result.talkvia_authorization && result.status === 'authorization_required'
  )
);
const sharedAuthorization = computed(
  () =>
    sharedAuthorizationResults.value.find(
      result => result.talkvia_authorization?.url
    ) || sharedAuthorizationResults.value[0]
);
const journeyIsActive = ref(false);
const userInitiatedBatchIds = new Set();

const BANK_CATALOG = [
  { code: 'novo_saque', label: 'Novo Saque', icon: 'NS' },
  { code: 'bank321', label: '321Bank', icon: '32' },
  { code: 'c6', label: 'C6', icon: 'C6' },
  { code: 'bmg', label: 'Banco BMG', icon: 'BG' },
  { code: 'handplus', label: 'Hand+', icon: 'H+' },
  { code: 'facta', label: 'Facta', icon: 'FA' },
  { code: 'presenca_bank', label: 'Presença Bank', icon: 'PB' },
  { code: 'v8', label: 'V8', icon: 'V8' },
  { code: 'soma', label: 'Soma', icon: 'SO' },
  { code: 'fintech_corban', label: 'Fintech do Corban', icon: 'FC' },
];

const PRODUCT_OPTIONS = ['CLT'];
const BASE_CUSTOMER_FIELDS = ['name', 'phone', 'birth_date'];
const GENDER_OPTIONS = [
  { value: '', label: 'Selecione' },
  { value: 'female', label: 'Feminino' },
  { value: 'male', label: 'Masculino' },
];

const BANK_REQUIREMENTS = {
  bmg: ['phone'],
  soma: ['name', 'phone'],
  v8: ['name', 'phone', 'birth_date', 'email'],
  facta: ['birth_date', 'amount', 'installment'],
};

const providerCode = provider => {
  const code = String(
    provider?.provider ||
      provider?.code ||
      provider?.value ||
      provider?.id ||
      ''
  )
    .trim()
    .toLowerCase();

  return code === 'prata' ? 'presenca_bank' : code;
};

const providerByCode = computed(() => {
  const map = new Map();
  providers.value.forEach(provider => {
    const code = providerCode(provider);
    if (code) map.set(code, provider);
  });
  return map;
});

const getProviderProducts = provider => {
  if (Array.isArray(provider?.products)) return provider.products;
  if (Array.isArray(provider?.produtos)) return provider.produtos;
  if (provider?.product || provider?.produto) {
    return [provider.product || provider.produto];
  }
  return [];
};

const getProviderCredentialStatus = provider => {
  if (provider?.credential_status) return provider.credential_status;
  if (provider?.credentials_status) return provider.credentials_status;
  return provider ? 'valid' : 'pending';
};

const getBankStatus = (active, provider) => {
  if (active) return 'active';
  return provider ? 'disabled' : 'pending';
};

// Providers ativos que ainda não constam do catálogo local também aparecem,
// para que um banco recém-integrado não fique oculto da seleção.
const bankCatalog = computed(() => {
  const knownCodes = new Set(BANK_CATALOG.map(bank => bank.code));
  const extraBanks = [];
  providerByCode.value.forEach((provider, code) => {
    if (knownCodes.has(code)) return;
    knownCodes.add(code);
    const label = String(provider?.label || provider?.name || code).trim();
    extraBanks.push({
      code,
      label,
      icon: label.slice(0, 2).toUpperCase(),
    });
  });
  return BANK_CATALOG.concat(extraBanks);
});

const bankCards = computed(() =>
  bankCatalog.value.map(bank => {
    const provider = providerByCode.value.get(bank.code);
    const configuredProducts = getProviderProducts(provider);
    const normalizedProducts = configuredProducts.map(product =>
      String(product).toUpperCase()
    );
    const active = Boolean(provider?.active ?? provider?.enabled ?? provider);
    const credentialStatus = getProviderCredentialStatus(provider);

    return {
      ...bank,
      provider,
      active,
      status: getBankStatus(active, provider),
      products: PRODUCT_OPTIONS.map(product => ({
        value: product,
        enabled:
          normalizedProducts.includes(product) ||
          (provider && !normalizedProducts.length),
      })),
      credentialStatus,
      lastTest:
        provider?.last_test_at ||
        provider?.last_validation_at ||
        provider?.updated_at ||
        null,
    };
  })
);

const activeBankCards = computed(() =>
  bankCards.value.filter(bank => bank.active)
);
const eligibleBankCards = computed(() =>
  activeBankCards.value.filter(bank =>
    bank.products.some(
      product => product.value === selectedProduct.value && product.enabled
    )
  )
);
const selectedBankCards = computed(() =>
  eligibleBankCards.value.filter(bank =>
    selectedHookIds.value.includes(Number(bank.provider?.hook_id))
  )
);
const requiredCustomerFields = computed(() =>
  Array.from(
    new Set(
      BASE_CUSTOMER_FIELDS.concat(
        selectedBankCards.value.flatMap(
          bank => BANK_REQUIREMENTS[bank.code] || []
        )
      )
    )
  )
);
const needsCustomerDetails = computed(
  () => requiredCustomerFields.value.length > 0
);
const fieldIsRequired = field => requiredCustomerFields.value.includes(field);

const formatPhone = value => {
  const digits = onlyDigits(value, 11);
  if (!digits) return '';
  if (digits.length < 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  const subscriberLength = digits.length === 11 ? 5 : 4;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 2 + subscriberLength)}-${digits.slice(2 + subscriberLength)}`;
};

const cpfInput = computed({
  get: () => formatCpf(cpf.value),
  set: value => {
    cpf.value = onlyDigits(value, 11);
  },
});

const phoneInput = computed({
  get: () => formatPhone(customer.value.phone),
  set: value => {
    const pastedDigits = onlyDigits(value, 13);
    customer.value.phone =
      pastedDigits.startsWith('55') && pastedDigits.length > 11
        ? pastedDigits.slice(2, 13)
        : pastedDigits.slice(0, 11);
  },
});

const birthDateInput = computed({
  get: () => formatBrazilianDate(customer.value.birth_date),
  set: value => {
    customer.value.birth_date = formatBrazilianDate(value);
  },
});

const getCurrencyInput = field =>
  computed({
    get: () => formatCurrencyInput(customer.value[field]),
    set: value => {
      customer.value[field] = currencyInputToDecimal(value);
    },
  });

const normalizedCpf = computed(() => {
  const digits = onlyDigits(cpf.value, 11);
  return digits.length >= 9 ? digits.padStart(11, '0') : '';
});

const comparableCpf = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 9 && digits.length <= 11
    ? digits.padStart(11, '0')
    : digits;
};

const contactCpf = contact =>
  comparableCpf(
    contact?.talkvia_profile?.cpf ||
      contact?.custom_attributes?.cpf ||
      contact?.custom_attributes?.CPF ||
      contact?.additional_attributes?.cpf ||
      contact?.additional_attributes?.CPF
  );

const contactBirthDate = contact => {
  const value = String(
    contact?.talkvia_profile?.birth_date ||
      contact?.custom_attributes?.data_nascimento ||
      contact?.custom_attributes?.birth_date ||
      contact?.additional_attributes?.data_nascimento ||
      contact?.additional_attributes?.birth_date ||
      ''
  ).trim();
  const brazilianDate = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brazilianDate) {
    return `${brazilianDate[3]}-${brazilianDate[2]}-${brazilianDate[1]}`;
  }
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
};

const contactPhone = contact => {
  const digits = String(contact?.phone_number || '').replace(/\D/g, '');
  return digits.length >= 12 && digits.startsWith('55')
    ? digits.slice(2, 13)
    : digits.slice(0, 11);
};

const clearAutoFilledCustomer = () => {
  Object.entries(autoFilledCustomer).forEach(([field, value]) => {
    if (customer.value[field] === value) customer.value[field] = '';
  });
  autoFilledCustomer = {};
  selectedContactId.value = null;
};

const fillCustomerFromContact = contact => {
  const values = {
    name: String(contact?.name || '').trim(),
    phone: contactPhone(contact),
    birth_date: contactBirthDate(contact),
    email: String(contact?.email || '').trim(),
  };
  autoFilledCustomer = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value)
  );
  Object.assign(customer.value, autoFilledCustomer);
  selectedContactId.value = contact.id;
  contactLookupMessage.value = `Contato encontrado: ${contact.name || 'dados preenchidos'}.`;
  isCustomerDetailsExpanded.value = true;
};

const lookupContact = value => {
  window.clearTimeout(contactLookupTimer);
  contactLookupSequence += 1;
  const lookupSequence = contactLookupSequence;
  clearAutoFilledCustomer();
  contactLookupMessage.value = '';
  contactLookupStatus.value = 'idle';
  if (!isValidCpf(value)) {
    if (value.length === 11)
      contactLookupMessage.value = 'Informe um CPF válido.';
    return;
  }
  if (props.initialContact?.id) {
    const existingCpf = contactCpf(props.initialContact);
    if (existingCpf && existingCpf !== value) {
      contactLookupStatus.value = 'error';
      contactLookupMessage.value =
        'O CPF deve corresponder ao contato deste atendimento.';
      return;
    }
    fillCustomerFromContact(props.initialContact);
    contactLookupStatus.value = 'found';
    return;
  }
  contactLookupStatus.value = 'loading';
  contactLookupMessage.value = 'Verificando cadastro do CPF…';

  contactLookupTimer = window.setTimeout(async () => {
    try {
      const contact = await findContact(
        value,
        item => contactCpf(item) === value
      );
      if (lookupSequence !== contactLookupSequence) return;
      contactLookupStatus.value = contact ? 'found' : 'missing';
      if (contact) fillCustomerFromContact(contact);
      else {
        contactLookupMessage.value =
          'CPF não cadastrado. Informe o celular com DDD; o contato será cadastrado ao consultar.';
        isCustomerDetailsExpanded.value = true;
      }
    } catch {
      if (lookupSequence === contactLookupSequence) {
        contactLookupStatus.value = 'error';
        contactLookupMessage.value =
          'Não foi possível verificar o cadastro. Tente novamente.';
      }
    }
  }, 350);
};
watch(normalizedCpf, lookupContact, { flush: 'sync' });

const activeBanksCount = computed(
  () => bankCards.value.filter(bank => bank.active).length
);
const configuredProductsCount = computed(() =>
  bankCards.value.reduce(
    (total, bank) =>
      total + bank.products.filter(product => product.enabled).length,
    0
  )
);

const providerText = computed(() => {
  const activeProviders = bankCards.value.filter(bank => bank.active);
  if (!activeProviders.length) return 'Nenhum banco conectado ativo';
  return activeProviders.map(provider => provider.label).join(', ');
});

const selectedCpf = computed(() => {
  if (!selectedBatch.value) return '';
  return showCpf.value && selectedBatch.value.cpf_full
    ? selectedBatch.value.cpf_full
    : selectedBatch.value.cpf;
});

const groupedBatches = computed(() => {
  const groups = new Map();
  batches.value.forEach(batch => {
    const key = comparableCpf(batch.cpf_full) || batch.cpf || String(batch.id);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        cpf: batch.cpf,
        cpfFull: batch.cpf_full,
        batches: [],
      });
    }
    groups.get(key).batches.push(batch);
  });
  return Array.from(groups.values());
});

const isRecentCpfVisible = key => visibleRecentCpfKeys.value.has(key);

const toggleRecentCpf = key => {
  const next = new Set(visibleRecentCpfKeys.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  visibleRecentCpfKeys.value = next;
};

const isRecentCpfCompact = key => compactRecentCpfKeys.value.has(key);

const toggleRecentCpfCompact = key => {
  const nextKeys = new Set(compactRecentCpfKeys.value);
  if (nextKeys.has(key)) nextKeys.delete(key);
  else nextKeys.add(key);
  compactRecentCpfKeys.value = nextKeys;
};

const recentCpfLabel = group =>
  isRecentCpfVisible(group.key) && group.cpfFull
    ? formatCpf(group.cpfFull)
    : group.cpf;

const groupIsSelected = group =>
  group.batches.some(batch => batch.id === selectedBatch.value?.id);

const canCreate = computed(
  () =>
    isValidCpf(normalizedCpf.value) &&
    selectedBankCards.value.length > 0 &&
    (contactLookupStatus.value === 'found' ||
      (contactLookupStatus.value === 'missing' &&
        isValidMobile(customer.value.phone)))
);

const statusLabels = {
  awaiting: 'Aguardando',
  processing: 'Processando',
  paused: 'Pausado',
  cancelled: 'Cancelado',
  completed: 'Concluído',
  partial_success: 'Concluído com erro',
  failed: 'Erro',
  timeout: 'Timeout',
  success: 'Sucesso',
  authorization_required: 'Autorização pendente',
  no_margin: 'Sem margem',
  no_offer: 'Sem oferta',
  missing_fields: 'Dados necessários',
  credentials_missing: 'Credencial ausente',
  product_not_supported: 'Produto indisponível',
  provider_error: 'Falha no banco',
  error: 'Erro',
};

const normalizeStatus = status => statusLabels[status] || status;

const statusClass = status => ({
  'is-success': ['completed', 'success'].includes(status),
  'is-processing': status === 'processing',
  'is-waiting': ['awaiting', 'paused'].includes(status),
  'is-warning': [
    'partial_success',
    'authorization_required',
    'no_margin',
    'no_offer',
    'missing_fields',
  ].includes(status),
  'is-error': [
    'failed',
    'error',
    'timeout',
    'cancelled',
    'credentials_missing',
    'provider_error',
    'product_not_supported',
  ].includes(status),
});

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatJson = payload => JSON.stringify(payload || {}, null, 2);

const FRIENDLY_LOG_FIELDS = {
  elegivel: 'Cliente elegível',
  eligible: 'Cliente elegível',
  aprovado: 'Aprovado',
  approved: 'Aprovado',
  cpf: 'CPF consultado',
  cpfi: 'CPF consultado',
  nome: 'Nome do cliente',
  name: 'Nome do cliente',
  margem: 'Margem disponível',
  margem_disponivel: 'Margem disponível',
  available_margin: 'Margem disponível',
  valor_liberado: 'Valor disponível',
  released_amount: 'Valor disponível',
  valor_parcela: 'Valor da parcela',
  prazo: 'Quantidade de parcelas',
  taxa: 'Taxa',
  banco: 'Banco',
  beneficio: 'Benefício',
  matricula: 'Matrícula',
};

const FRIENDLY_MESSAGE_FIELDS = new Set([
  'disclaimer',
  'aviso',
  'message',
  'mensagem',
  'motivo',
  'reason',
]);

const visitLogValues = (payload, callback) => {
  // Lists of products and offers are rendered separately, never as customer facts.
  if (Array.isArray(payload)) return;
  if (!payload || typeof payload !== 'object') return;

  Object.entries(payload).forEach(([key, value]) => {
    if (value && typeof value === 'object') visitLogValues(value, callback);
    else callback(String(key).toLowerCase(), value);
  });
};

const firstLogValue = keys => {
  let found;
  visitLogValues(selectedLog.value?.raw_response, (key, value) => {
    if (found === undefined && keys.includes(key)) found = value;
  });
  return found;
};

const maskLogCpf = value => {
  const digits = onlyDigits(value, 11);
  if (digits.length !== 11) return 'CPF protegido';
  return `${digits.slice(0, 3)}.***.***-${digits.slice(-2)}`;
};

const formatFriendlyLogValue = (key, value) => {
  if (['cpf', 'cpfi'].includes(key)) return maskLogCpf(value);
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (/margem|valor_liberado|released_amount|valor_parcela/.test(key)) {
    return formatCurrency(value);
  }
  if (key === 'taxa') return formatRate(value);
  return String(value);
};

const friendlyLogFacts = computed(() => {
  const facts = [];
  visitLogValues(selectedLog.value?.raw_response, (key, value) => {
    const label = FRIENDLY_LOG_FIELDS[key];
    if (!label || value === null || value === undefined || value === '') return;
    const formattedValue = formatFriendlyLogValue(key, value);
    if (
      facts.some(fact => fact.label === label && fact.value === formattedValue)
    )
      return;
    facts.push({ label, value: formattedValue });
  });
  return facts.slice(0, 16);
});

const friendlyLogMessages = computed(() => {
  const messages = [];
  visitLogValues(selectedLog.value?.raw_response, (key, value) => {
    if (!FRIENDLY_MESSAGE_FIELDS.has(key) || typeof value !== 'string') return;
    const message = apiErrorMessage({ message: value }, '').trim();
    if (message && !messages.includes(message)) messages.push(message);
  });
  return messages.slice(0, 3);
});

const selectedLogOutcome = computed(() => {
  if (selectedLog.value?.offers?.length)
    return 'Ofertas disponíveis para análise';
  const eligible = firstLogValue(['elegivel', 'eligible']);
  const approved = firstLogValue(['aprovado', 'approved']);
  if (eligible === false) return 'Cliente não elegível';
  if (eligible === true) return 'Cliente elegível';
  if (approved === false) return 'Não aprovado';
  if (approved === true) return 'Aprovado';
  if (selectedLog.value?.status === 'success')
    return 'Consulta concluída com sucesso';
  return resultReason(selectedLog.value);
});

const openResultDetails = result => {
  selectedLog.value = result;
  showTechnicalDetails.value = false;
};

const closeResultDetails = () => {
  selectedLog.value = null;
  showTechnicalDetails.value = false;
};

const formatDuration = value => {
  if (!value && value !== 0) return '-';
  if (value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(1)} s`;
};

const formatCurrency = value => {
  const text = String(value ?? '').trim();
  const amount = Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
  );
  if (!Number.isFinite(amount)) return text || '-';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
};

const actionFieldLabels = {
  name: 'Nome completo',
  phone: 'Telefone',
  birth_date: 'Data de nascimento',
  email: 'E-mail',
  gender: 'Sexo',
  banking_provider: 'Bancarizadora',
  income: 'Renda mensal',
  amount: 'Valor desejado',
  installment: 'Valor da parcela',
  term: 'Prazo (meses)',
  token: 'Código recebido por SMS',
};

const actionFieldType = field => {
  if (field === 'birth_date') return 'date';
  if (field === 'email') return 'email';
  if (['income', 'amount', 'installment', 'term'].includes(field))
    return 'number';
  if (field === 'token') return 'password';
  return 'text';
};

const actionFields = computed(
  () => actionResult.value?.next_action?.required_fields || []
);
const actionCanSubmit = computed(
  () =>
    actionResult.value?.next_action?.available !== false &&
    actionFields.value.every(field =>
      String(actionForm.value[field] || '').trim()
    )
);

const actionUnavailableMessage = computed(() => {
  if (actionResult.value?.next_action?.unavailable_reason) {
    return actionResult.value.next_action.unavailable_reason;
  }
  return actionResult.value?.next_action?.type === 'token'
    ? 'A validação segura do código ainda não está disponível.'
    : 'A integração não disponibilizou uma operação para gerar esta autorização.';
});

const actionModalDescription = computed(() => {
  const type = actionResult.value?.next_action?.type;
  if (type === 'authorization') {
    return 'Preencha os dados para gerar o link de consentimento do banco.';
  }
  if (type === 'otp_request') {
    if (!actionFields.value.length) {
      return 'Solicite o código OTP para o celular informado nos dados adicionais do cliente.';
    }
    return 'Informe o celular do cliente para solicitar o código OTP do banco.';
  }
  if (type === 'token') {
    return 'Informe o código OTP recebido pelo cliente para validar a consulta.';
  }
  if (type === 'offer_query') {
    return 'A autorização foi confirmada. Busque agora as condições de crédito disponíveis na C6.';
  }
  if (type === 'simulation') {
    return 'Confirme para usar a margem retornada pelo banco e calcular a oferta.';
  }
  return 'Preencha os dados solicitados pelo banco.';
});

const actionSubmitLabel = computed(() => {
  const type = actionResult.value?.next_action?.type;
  if (type === 'authorization') return 'Gerar e abrir link';
  if (type === 'otp_request') return 'Solicitar código OTP';
  if (type === 'token') return 'Validar código OTP';
  if (type === 'offer_query') return 'Buscar oferta C6';
  if (type === 'simulation') return 'Simular oferta';
  return 'Continuar consulta';
});

const openResultAction = result => {
  userInitiatedBatchIds.add(selectedBatch.value.id);
  journeyIsActive.value = true;
  delete continuationErrors.value[result.id];
  automaticContinuationAttempts.delete(
    automaticActionKey(selectedBatch.value.id, result)
  );
  queryStatusAttempts.delete(
    automaticActionKey(selectedBatch.value.id, result)
  );
  if (result.next_action?.type === 'authorization') {
    if (result.authorization_link) {
      window.open(result.authorization_link, '_blank', 'noopener,noreferrer');
      return;
    }
  }

  actionResult.value = result;
  actionForm.value = Object.fromEntries(
    (result.next_action?.required_fields || []).map(field => [field, ''])
  );
  if (!(result.next_action?.required_fields || []).length) submitResultAction();
};

const copyAuthorizationLink = async result => {
  if (!result?.authorization_link || copyingAuthorizationResultId.value) return;

  userInitiatedBatchIds.add(selectedBatch.value.id);
  journeyIsActive.value = true;
  copyingAuthorizationResultId.value = result.id;
  try {
    await navigator.clipboard.writeText(result.authorization_link);
    openFeedback(
      'Link copiado',
      'O link de autorização foi copiado para a área de transferência.'
    );
  } catch {
    openFeedback(
      'Não foi possível copiar o link',
      'Copie o endereço depois de abrir a autorização em uma nova aba.'
    );
  } finally {
    copyingAuthorizationResultId.value = null;
  }
};

const closeActionModal = () => {
  if (isSubmittingAction.value) return;
  actionResult.value = null;
  actionForm.value = {};
};

const refreshFailedResult = async (result, error) => {
  continuationErrors.value[result.id] = apiErrorMessage(
    error,
    'Não foi possível concluir a consulta.'
  );
  const batchId = selectedBatch.value?.id;
  if (!batchId) return;
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.show(batchId);
    if (!disposed && selectedBatch.value?.id === batchId) {
      selectedBatch.value = data;
    }
  } catch {
    // Keep the local error visible if refreshing the saved result fails.
  }
};

const submitResultAction = async () => {
  if (!actionCanSubmit.value || !selectedBatch.value?.id) return;
  userInitiatedBatchIds.add(selectedBatch.value.id);
  journeyIsActive.value = true;
  const shouldOpenAuthorization =
    actionResult.value?.next_action?.type === 'authorization';
  const authorizationWindow = shouldOpenAuthorization
    ? window.open('about:blank', '_blank')
    : null;
  if (authorizationWindow) authorizationWindow.opener = null;
  isSubmittingAction.value = true;
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.continueResult(
      selectedBatch.value.id,
      actionResult.value.id,
      actionForm.value
    );
    selectedBatch.value = data;
    const updatedResult = data.results?.find(
      result => result.id === actionResult.value.id
    );
    if (shouldOpenAuthorization && updatedResult?.authorization_link) {
      if (authorizationWindow) {
        authorizationWindow.location.href = updatedResult.authorization_link;
      } else {
        window.open(
          updatedResult.authorization_link,
          '_blank',
          'noopener,noreferrer'
        );
      }
    } else {
      authorizationWindow?.close();
    }
    isSubmittingAction.value = false;
    closeActionModal();
    await loadBatches();
  } catch (error) {
    authorizationWindow?.close();
    if (actionResult.value)
      await refreshFailedResult(actionResult.value, error);
    openFeedback(
      'Não foi possível continuar a consulta',
      apiErrorMessage(error, 'Revise os dados e tente novamente.')
    );
  } finally {
    isSubmittingAction.value = false;
  }
};

const canRetryResult = result =>
  result && !['awaiting', 'processing'].includes(result.status);

const retryResult = async result => {
  if (
    !canRetryResult(result) ||
    !selectedBatch.value?.id ||
    retryingResultId.value
  )
    return;

  retryingResultId.value = result.id;
  userInitiatedBatchIds.add(selectedBatch.value.id);
  journeyIsActive.value = true;
  automaticContinuationAttempts.delete(
    automaticActionKey(selectedBatch.value.id, result)
  );
  queryStatusAttempts.delete(
    automaticActionKey(selectedBatch.value.id, result)
  );
  delete continuationErrors.value[result.id];
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.retryResult(
      selectedBatch.value.id,
      result.id
    );
    selectedBatch.value = data;
    await loadBatches();
  } catch (error) {
    await refreshFailedResult(result, error);
    openFeedback(
      'Não foi possível reconsultar o banco',
      apiErrorMessage(error, 'Verifique a integração e tente novamente.')
    );
  } finally {
    retryingResultId.value = null;
  }
};

const formatRate = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? `${parsed.toLocaleString('pt-BR')}%` : value;
};

const rejectedStatuses = [
  'failed',
  'error',
  'timeout',
  'cancelled',
  'no_margin',
  'no_offer',
  'missing_fields',
  'credentials_missing',
  'provider_error',
];

const isRejectedResult = result => rejectedStatuses.includes(result?.status);

const resultReason = result =>
  result?.error_detail ||
  result?.error_message ||
  result?.response_summary ||
  'O banco não informou um motivo detalhado.';

const openFeedback = async (title, message) => {
  feedback.value = { title, message };
  await nextTick();
  feedbackDialogRef.value?.open();
};

const apiErrorMessage = (error, fallback) => {
  const message = String(
    error?.response?.data?.error || error?.message || fallback
  );
  if (/invalid_or_inactive_(?:chatwoot|talkvia)_user/i.test(message)) {
    return 'Seu acesso à consulta bancária está inativo ou não está autorizado. Entre novamente na Talkvia e tente outra vez.';
  }
  if (/bypass_failed/i.test(message)) {
    return 'O banco não conseguiu concluir a autorização. O aceite registrado na Talkvia foi preservado; tente novamente ou solicite a revisão da integração ao suporte.';
  }
  if (
    /dataNascimento|data_nascimento/i.test(message) &&
    /convert|invalid|valida|format/i.test(message)
  ) {
    return 'O banco não conseguiu interpretar a data de nascimento. Confira a data do cliente e tente novamente. Se a data estiver correta, solicite a revisão da integração ao suporte.';
  }
  if (/JSON value could not be converted|BytePositionInLine/i.test(message)) {
    return 'O banco recusou os dados enviados. Confira os dados do cliente e tente novamente.';
  }
  return message.replace(/chatwoot/gi, 'Talkvia');
};

const toggleBank = bank => {
  const hookId = Number(bank.provider?.hook_id);
  if (!hookId) return;
  selectedHookIds.value = selectedHookIds.value.includes(hookId)
    ? selectedHookIds.value.filter(id => id !== hookId)
    : [...selectedHookIds.value, hookId];
};

const selectOnlyBank = bank => {
  const hookId = Number(bank.provider?.hook_id);
  if (!hookId) return;
  selectedHookIds.value = [hookId];
};

const selectAllEligibleBanks = () => {
  selectedHookIds.value = eligibleBankCards.value
    .map(bank => Number(bank.provider?.hook_id))
    .filter(Boolean);
};

const clearBankSelection = () => {
  selectedHookIds.value = [];
};

const openBankingSettings = () => {
  router.push(accountScopedRoute('settings_integrations_banking'));
};

watch(selectedBatch, batch => emit('batchChange', batch), { deep: true });
watch(
  () =>
    Boolean(
      isCreating.value ||
        isSubmittingAction.value ||
        isContinuingAutomatically.value ||
        retryingResultId.value
    ),
  busy => emit('busyChange', busy)
);

const loadBatches = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.get(
      props.initialContact?.id
        ? { contact_id: props.initialContact.id }
        : undefined
    );
    if (disposed) return;
    batches.value = data.batches || [];
    providers.value = data.providers || [];
    if (!selectedHookIds.value.length) {
      selectedHookIds.value = (data.providers || [])
        .map(provider => Number(provider.hook_id))
        .filter(Boolean);
    }
    if (!selectedBatch.value && batches.value.length) {
      await selectBatch(batches.value[0]);
    }
  } catch (error) {
    openFeedback(
      'Não foi possível carregar as consultas',
      apiErrorMessage(error, 'Tente novamente em alguns instantes.')
    );
  } finally {
    isLoading.value = false;
  }
};

const selectBatch = async (batch, { continueUserFlow = false } = {}) => {
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.show(batch.id);
    if (disposed) return;
    selectedBatch.value = data;
    journeyIsActive.value = userInitiatedBatchIds.has(data.id);
    showCpf.value = false;
    if (continueUserFlow && userInitiatedBatchIds.has(data.id)) {
      await continueAutomaticActions(data);
    }
  } catch (error) {
    openFeedback(
      'Não foi possível abrir a consulta',
      apiErrorMessage(error, 'Tente novamente em alguns instantes.')
    );
  }
};

const continueAutomaticActions = async batch => {
  if (
    disposed ||
    isContinuingAutomatically.value ||
    isSubmittingAction.value ||
    actionResult.value
  )
    return;
  const candidates = (batch?.results || []).filter(canContinueAutomatically);

  const pendingCandidates = candidates.filter(result => {
    const attemptKey = automaticActionKey(batch.id, result);
    if (automaticContinuationAttempts.has(attemptKey)) return false;

    if (result.next_action.type === 'query_status') {
      const attempts = queryStatusAttempts.get(attemptKey) || 0;
      if (attempts >= 30) {
        continuationErrors.value[result.id] =
          'A V8 continua em análise. Use “Atualizar resultado” para acompanhar novamente.';
        return false;
      }
      queryStatusAttempts.set(attemptKey, attempts + 1);
    } else {
      automaticContinuationAttempts.add(attemptKey);
    }
    return true;
  });

  if (!pendingCandidates.length) return;
  isContinuingAutomatically.value = true;
  const attempts = pendingCandidates.map(async result => {
    delete continuationErrors.value[result.id];
    try {
      await TalkviaConnectedBankQueriesAPI.continueResult(
        batch.id,
        result.id,
        {}
      );
    } catch (error) {
      automaticContinuationAttempts.add(automaticActionKey(batch.id, result));
      continuationErrors.value[result.id] = apiErrorMessage(
        error,
        'Não foi possível continuar. Tente novamente neste banco.'
      );
    }
  });
  try {
    await Promise.all(attempts);
    if (selectedBatch.value?.id === batch.id) {
      const { data } = await TalkviaConnectedBankQueriesAPI.show(batch.id);
      pendingCandidates.forEach(attempt => {
        const updated = data.results?.find(result => result.id === attempt.id);
        if (
          attempt.next_action.type === 'authorization_sync' &&
          updated?.next_action?.type === 'authorization_sync' &&
          !continuationErrors.value[attempt.id]
        ) {
          continuationErrors.value[attempt.id] =
            updated.error_detail ||
            updated.error_message ||
            'O banco ainda solicita autorização após receber o aceite. Tente novamente ou verifique a integração.';
        }
      });
      if (selectedBatch.value?.id === batch.id) selectedBatch.value = data;
    }
  } finally {
    isContinuingAutomatically.value = false;
  }
};

const resumeJourney = async () => {
  if (!selectedBatch.value) return;
  userInitiatedBatchIds.add(selectedBatch.value.id);
  journeyIsActive.value = true;
  await refreshSelected();
};

const createBatch = async () => {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;
  try {
    const queryCpf = normalizedCpf.value;
    const queryCustomer = { ...customer.value };
    let contactId = selectedContactId.value;
    if (!contactId) {
      const contact = await registerBankContact(
        queryCpf,
        queryCustomer,
        contactCpf
      ).catch(error => {
        if (!error.response) throw error;
        throw new Error(
          'Não foi possível concluir o cadastro. Verifique se o celular ou e-mail já pertence a outro contato e tente novamente.'
        );
      });
      contactId = contact.id;
      if (normalizedCpf.value !== queryCpf) return;
      selectedContactId.value = contactId;
      contactLookupStatus.value = 'found';
      contactLookupMessage.value =
        'Contato cadastrado. Você já pode consultar.';
    }
    const { data } = await TalkviaConnectedBankQueriesAPI.create({
      cpf: queryCpf,
      product: selectedProduct.value,
      hook_ids: selectedBankCards.value.map(bank => bank.provider.hook_id),
      providers: selectedBankCards.value.map(bank => bank.code),
      customer: {
        ...queryCustomer,
        birth_date: brazilianDateToIso(queryCustomer.birth_date),
      },
      contact_id: contactId,
      ...(props.conversationId
        ? { conversation_id: props.conversationId }
        : {}),
    });
    if (disposed) return;
    selectedBatch.value = data;
    userInitiatedBatchIds.add(data.id);
    journeyIsActive.value = true;
    showCpf.value = false;
    if (!props.embedded) cpf.value = '';
    await continueAutomaticActions(data);
    await loadBatches();
  } catch (error) {
    openFeedback(
      'Não foi possível iniciar a consulta',
      apiErrorMessage(error, 'Revise os dados e tente novamente.')
    );
  } finally {
    isCreating.value = false;
  }
};

const openBureauQuery = () => {
  if (normalizedCpf.value) bureauDialogRef.value?.open(normalizedCpf.value);
};

const fillCustomerFromBureau = ({ cpf: queriedCpf, person }) => {
  if (queriedCpf !== normalizedCpf.value || !person) return;
  const phone = (person.celulares || []).find(item =>
    isValidMobile(`${item.ddd || ''}${item.numero || ''}`)
  );
  const values = {
    name: person.nome || '',
    birth_date: formatBrazilianDate(person.data_nascimento || ''),
    phone: phone ? `${phone.ddd || ''}${phone.numero || ''}` : '',
    mother_name: person.nome_mae || '',
  };
  Object.entries(values).forEach(([field, value]) => {
    if (value && !customer.value[field]) {
      customer.value[field] = value;
      autoFilledCustomer[field] = value;
    }
  });
  isCustomerDetailsExpanded.value = true;
};

const requestDeleteBatch = batch => {
  if (!batch?.id) return;
  pendingDeleteBatch.value = batch;
  deleteDialogRef.value?.open();
};

const closeDeleteDialog = () => {
  deleteDialogRef.value?.close();
  pendingDeleteBatch.value = null;
};

const deleteBatch = async () => {
  const batch = pendingDeleteBatch.value;
  if (!batch?.id) return;

  deletingBatchId.value = batch.id;
  try {
    await TalkviaConnectedBankQueriesAPI.delete(batch.id);
    closeDeleteDialog();
    if (selectedBatch.value?.id === batch.id) selectedBatch.value = null;
    await loadBatches();
    await openFeedback(
      'Consulta excluída',
      `A consulta ${batch.cpf} e seus retornos foram removidos.`
    );
  } catch (error) {
    closeDeleteDialog();
    await openFeedback(
      'Não foi possível excluir a consulta',
      apiErrorMessage(error, 'Tente novamente em alguns instantes.')
    );
  } finally {
    deletingBatchId.value = null;
  }
};

const requestDeleteCpfGroup = group => {
  if (!group?.cpfFull) return;
  pendingDeleteCpfGroup.value = group;
  deleteCpfDialogRef.value?.open();
};

const deleteCpfGroup = async () => {
  const group = pendingDeleteCpfGroup.value;
  if (!group?.cpfFull) return;

  deletingCpfKey.value = group.key;
  try {
    const { data } = await TalkviaConnectedBankQueriesAPI.deleteByCpf(
      group.cpfFull
    );
    deleteCpfDialogRef.value?.close();
    pendingDeleteCpfGroup.value = null;
    if (groupIsSelected(group)) selectedBatch.value = null;
    await loadBatches();
    await openFeedback(
      'Consultas do CPF excluídas',
      `${data.deleted_count || 0} consulta(s) de ${group.cpf} e todos os retornos dos bancos foram removidos.`
    );
  } catch (error) {
    deleteCpfDialogRef.value?.close();
    pendingDeleteCpfGroup.value = null;
    await openFeedback(
      'Não foi possível excluir as consultas do CPF',
      apiErrorMessage(error, 'Tente novamente em alguns instantes.')
    );
  } finally {
    deletingCpfKey.value = null;
  }
};

const refreshSelected = async () => {
  if (!selectedBatch.value?.id || isContinuingAutomatically.value) return;
  await selectBatch(selectedBatch.value, {
    continueUserFlow: userInitiatedBatchIds.has(selectedBatch.value.id),
  });
  const active = ['awaiting', 'processing'].includes(
    selectedBatch.value?.status
  );
  if (!active) await loadBatches();
};

onMounted(async () => {
  if (props.initialContact?.id) {
    cpf.value = contactCpf(props.initialContact);
    fillCustomerFromContact(props.initialContact);
    contactLookupStatus.value = isValidCpf(normalizedCpf.value)
      ? 'found'
      : 'idle';
  }
  await loadBatches();
  if (disposed) return;
  pollingTimer = window.setInterval(refreshSelected, 4000);
});

onBeforeUnmount(() => {
  disposed = true;
  contactLookupSequence += 1;
  if (pollingTimer) window.clearInterval(pollingTimer);
  if (contactLookupTimer) window.clearTimeout(contactLookupTimer);
});
</script>

<template>
  <BureauQueryDialog ref="bureauDialogRef" @result="fillCustomerFromBureau" />
  <main class="bank-query-page" :class="{ 'is-embedded': embedded }">
    <section v-if="!embedded" class="bank-hero">
      <div>
        <p class="eyebrow">Consulta bancária</p>
        <h1>Consulta aos bancos</h1>
        <p>
          Consulte o CPF nos bancos habilitados e acompanhe autorizações,
          margens e ofertas em um só lugar.
        </p>
      </div>
      <Button
        type="button"
        :label="activeBanksCount ? 'Gerenciar bancos' : 'Cadastrar bancos'"
        icon="i-lucide-plus"
        @click="openBankingSettings"
      />
    </section>

    <section v-if="!embedded" class="summary-grid">
      <article class="summary-card">
        <span class="summary-icon">
          <Icon icon="i-lucide-circle-check-big" class="size-5" />
        </span>
        <div>
          <strong>{{ activeBanksCount }}</strong
          ><small>Bancos ativos</small>
        </div>
      </article>
      <article class="summary-card">
        <span class="summary-icon">
          <Icon icon="i-lucide-landmark" />
        </span>
        <div>
          <strong>{{ bankCards.length }}</strong
          ><small>Bancos disponíveis</small>
        </div>
      </article>
      <article class="summary-card">
        <span class="summary-icon">
          <Icon icon="i-lucide-layers-3" class="size-5" />
        </span>
        <div>
          <strong>{{ configuredProductsCount }}</strong
          ><small>Produtos configurados</small>
        </div>
      </article>
    </section>

    <section class="query-card secondary-query-card">
      <div v-if="!activeBanksCount" class="no-banks-state">
        <span class="no-banks-state__icon">
          <Icon icon="i-lucide-landmark" class="size-6" />
        </span>
        <div>
          <strong>Nenhum banco ativo</strong>
          <p>
            Cadastre e ative ao menos um banco para realizar consultas de
            crédito consignado CLT.
          </p>
        </div>
        <Button
          type="button"
          label="Cadastrar bancos"
          icon="i-lucide-plus"
          @click="openBankingSettings"
        />
      </div>

      <div v-if="activeBanksCount" class="bank-selection-header">
        <button
          type="button"
          class="section-disclosure"
          :aria-expanded="isBankSelectionExpanded"
          @click="isBankSelectionExpanded = !isBankSelectionExpanded"
        >
          <Icon icon="i-lucide-landmark" />
          <span class="section-disclosure-copy">
            <strong>Bancos conectados</strong>
            <small>
              {{ selectedBankCards.length }} de {{ eligibleBankCards.length }}
              selecionado(s)
            </small>
          </span>
          <Icon
            :icon="
              isBankSelectionExpanded
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
            class="size-5"
          />
        </button>
        <div v-if="isBankSelectionExpanded" class="selection-actions">
          <button type="button" @click="selectAllEligibleBanks">
            Selecionar todos
          </button>
          <button type="button" @click="clearBankSelection">Limpar</button>
        </div>
      </div>

      <div
        v-if="
          activeBanksCount &&
          isBankSelectionExpanded &&
          eligibleBankCards.length
        "
        class="bank-selection-grid"
      >
        <article
          v-for="bank in eligibleBankCards"
          :key="bank.code"
          class="bank-option"
          :class="{
            'is-selected': selectedHookIds.includes(
              Number(bank.provider?.hook_id)
            ),
          }"
        >
          <button
            type="button"
            class="bank-option-toggle"
            :aria-pressed="
              selectedHookIds.includes(Number(bank.provider?.hook_id))
            "
            @click="toggleBank(bank)"
          >
            <BankLogo :provider="bank.code" />
            <span class="bank-option-copy">
              <strong>{{ bank.label }}</strong>
              <small>{{ selectedProduct }} configurado</small>
            </span>
            <Icon
              :icon="
                selectedHookIds.includes(Number(bank.provider?.hook_id))
                  ? 'i-lucide-circle-check-big'
                  : 'i-lucide-circle'
              "
              class="bank-check size-5"
            />
          </button>
          <button
            type="button"
            class="bank-only-action"
            @click="selectOnlyBank(bank)"
          >
            Consultar somente {{ bank.label }}
          </button>
        </article>
      </div>
      <div
        v-else-if="activeBanksCount && isBankSelectionExpanded"
        class="bank-empty-state"
      >
        <Icon icon="i-lucide-landmark" />
        Nenhum banco ativo oferece {{ selectedProduct }}. Ajuste as integrações
        para continuar.
      </div>

      <div
        v-if="activeBanksCount && needsCustomerDetails"
        class="customer-details"
      >
        <button
          type="button"
          class="customer-details-heading section-disclosure"
          :aria-expanded="isCustomerDetailsExpanded"
          @click="isCustomerDetailsExpanded = !isCustomerDetailsExpanded"
        >
          <Icon icon="i-lucide-user-round" class="size-5" />
          <span class="section-disclosure-copy">
            <strong>Dados adicionais do cliente</strong>
            <small>
              Estes campos aparecem somente quando um banco selecionado pode
              solicitá-los. Preencha apenas os dados disponíveis.
            </small>
          </span>
          <Icon
            :icon="
              isCustomerDetailsExpanded
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
            class="size-5"
          />
        </button>
        <div v-show="isCustomerDetailsExpanded" class="customer-grid">
          <Input
            v-if="fieldIsRequired('name')"
            v-model="customer.name"
            label="Nome completo (opcional)"
            placeholder="Nome do titular"
          />
          <Input
            v-if="fieldIsRequired('phone')"
            v-model="phoneInput"
            :label="
              contactLookupStatus === 'missing'
                ? 'Celular (obrigatório)'
                : 'Celular (opcional)'
            "
            inputmode="tel"
            maxlength="15"
            placeholder="(11) 99999-9999"
          />
          <Input
            v-if="fieldIsRequired('birth_date')"
            v-model="birthDateInput"
            label="Data de nascimento (opcional)"
            inputmode="numeric"
            maxlength="10"
            placeholder="dd/mm/aaaa"
          />
          <Input
            v-if="fieldIsRequired('email')"
            v-model="customer.email"
            label="E-mail (opcional)"
            type="email"
            placeholder="cliente@exemplo.com"
          />
          <Input
            v-if="fieldIsRequired('amount')"
            v-model="getCurrencyInput('amount').value"
            label="Valor desejado (opcional)"
            inputmode="decimal"
            placeholder="0,00"
          />
          <Input
            v-if="fieldIsRequired('installment')"
            v-model="getCurrencyInput('installment').value"
            label="Valor da parcela (opcional)"
            inputmode="decimal"
            placeholder="0,00"
          />
        </div>
      </div>

      <div v-if="activeBanksCount" class="query-action-row">
        <div class="query-field">
          <label for="connected-bank-cpf">CPF do cliente</label>
          <Input
            id="connected-bank-cpf"
            v-model="cpfInput"
            :readonly="Boolean(initialContact && contactCpf(initialContact))"
            inputmode="numeric"
            maxlength="14"
            placeholder="000.000.000-00"
          />
          <small class="query-field-hint">
            Digite de 9 a 11 números. Zeros iniciais são completados
            automaticamente.
          </small>
          <small
            v-if="contactLookupMessage"
            class="contact-lookup-message"
            role="status"
          >
            {{ contactLookupMessage }}
          </small>
          <Button
            v-if="contactLookupStatus === 'error'"
            label="Verificar CPF novamente"
            variant="link"
            @click="lookupContact(normalizedCpf)"
          />
        </div>
        <div class="query-actions">
          <Button
            class="query-submit"
            color="slate"
            variant="outline"
            :disabled="!normalizedCpf"
            label="Consultar nos birôs"
            icon="i-lucide-user-round-search"
            @click="openBureauQuery"
          />
          <Button
            class="query-submit"
            :disabled="!canCreate || isCreating"
            :is-loading="isCreating"
            :label="`Consultar ${selectedBankCards.length || ''} ${
              selectedBankCards.length === 1 ? 'banco' : 'bancos'
            }`"
            icon="i-lucide-search"
            @click="createBatch"
          />
        </div>
      </div>
      <p v-if="activeBanksCount" class="query-helper">{{ providerText }}</p>
    </section>

    <section class="query-grid">
      <aside class="recent-card">
        <div class="recent-header">
          <div>
            <h2>Consultas recentes</h2>
            <p>
              {{ batches.length }} consulta(s) · {{ groupedBatches.length }}
              CPF(s)
            </p>
          </div>
        </div>
        <article
          v-for="group in groupedBatches"
          :key="group.key"
          class="cpf-group-card"
          :class="{ 'is-selected': groupIsSelected(group) }"
        >
          <header class="cpf-group-header">
            <button
              class="cpf-group-title"
              type="button"
              @click="selectBatch(group.batches[0])"
            >
              <span class="cpf-group-icon">
                <Icon icon="i-lucide-contact-round" class="size-4" />
              </span>
              <span>
                <strong>{{ recentCpfLabel(group) }}</strong>
                <small>{{ group.batches.length }} consulta(s) neste CPF</small>
              </span>
            </button>
            <div class="cpf-group-actions">
              <Button
                v-if="group.cpfFull"
                type="button"
                :icon="
                  isRecentCpfVisible(group.key)
                    ? 'i-lucide-eye-off'
                    : 'i-lucide-eye'
                "
                color="slate"
                variant="ghost"
                size="xs"
                :title="
                  isRecentCpfVisible(group.key)
                    ? 'Ocultar CPF completo'
                    : 'Ver CPF completo'
                "
                @click="toggleRecentCpf(group.key)"
              />
              <Button
                v-if="group.cpfFull"
                type="button"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="xs"
                :is-loading="deletingCpfKey === group.key"
                :disabled="deletingCpfKey === group.key"
                title="Excluir todas as consultas deste CPF"
                @click="requestDeleteCpfGroup(group)"
              />
              <button
                type="button"
                class="cpf-group-compact"
                :title="
                  isRecentCpfCompact(group.key)
                    ? 'Expandir consultas deste CPF'
                    : 'Compactar consultas deste CPF'
                "
                @click="toggleRecentCpfCompact(group.key)"
              >
                <Icon
                  :icon="
                    isRecentCpfCompact(group.key)
                      ? 'i-lucide-unfold-vertical'
                      : 'i-lucide-fold-vertical'
                  "
                  class="size-4"
                />
                <span>
                  {{ isRecentCpfCompact(group.key) ? 'Expandir' : 'Compactar' }}
                </span>
              </button>
            </div>
          </header>
          <div v-if="!isRecentCpfCompact(group.key)" class="cpf-group-attempts">
            <article
              v-for="batch in group.batches"
              :key="batch.id"
              class="batch-attempt"
              :class="[
                statusClass(batch.status),
                selectedBatch?.id === batch.id ? 'is-selected' : '',
              ]"
            >
              <button
                class="batch-attempt-main"
                type="button"
                @click="selectBatch(batch)"
              >
                <span>
                  <strong>{{ formatDate(batch.created_at) }}</strong>
                  <small
                    >{{ batch.processed }}/{{ batch.total }} processados ·
                    {{ batch.success_count || 0 }} sucesso(s)</small
                  >
                </span>
                <span
                  class="status-pill compact"
                  :class="statusClass(batch.status)"
                >
                  {{ normalizeStatus(batch.status) }}
                </span>
              </button>
              <Button
                class="batch-attempt-delete"
                type="button"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="xs"
                :is-loading="deletingBatchId === batch.id"
                :disabled="deletingBatchId === batch.id"
                title="Excluir consulta"
                @click="requestDeleteBatch(batch)"
              />
            </article>
          </div>
        </article>
        <p v-if="!batches.length && !isLoading" class="empty">
          Nenhuma consulta iniciada.
        </p>
      </aside>

      <section class="detail-card">
        <template v-if="selectedBatch">
          <div class="detail-header">
            <div>
              <h2>{{ selectedBatch.title }}</h2>
              <p class="cpf-line">
                <span>{{ selectedCpf }}</span>
                <Button
                  v-if="selectedBatch.cpf_full"
                  type="button"
                  class="icon-inline"
                  :icon="showCpf ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  color="slate"
                  variant="ghost"
                  size="xs"
                  :title="showCpf ? 'Ocultar CPF' : 'Ver CPF completo'"
                  @click="showCpf = !showCpf"
                />
                <span
                  >- {{ selectedBatch.processed }}/{{
                    selectedBatch.total
                  }}
                  processados</span
                >
              </p>
            </div>
            <span
              class="status-pill"
              :class="statusClass(selectedBatch.status)"
            >
              {{ normalizeStatus(selectedBatch.status) }}
            </span>
          </div>

          <section
            v-if="selectedBatch.cross_sell"
            class="mb-4 space-y-2 rounded-xl border border-n-brand bg-n-solid-2 p-4"
          >
            <strong class="text-n-slate-12"
              >Próxima alternativa:
              {{ selectedBatch.cross_sell.product }}</strong
            >
            <p class="text-sm text-n-slate-11">
              As consultas de
              {{ selectedBatch.cross_sell.primary_product }} terminaram sem
              oferta. Você pode oferecer
              {{ selectedBatch.cross_sell.product }} ao cliente pelos bancos
              {{ selectedBatch.cross_sell.banks.join(', ') }}.
            </p>
            <p class="text-xs text-n-slate-11">
              Confirme o interesse do cliente e as autorizações do produto antes
              de prosseguir. Nenhuma nova consulta foi iniciada.
            </p>
          </section>
          <div class="progress">
            <span
              :style="{
                width: `${selectedBatch.total ? (selectedBatch.processed / selectedBatch.total) * 100 : 0}%`,
              }"
            />
          </div>

          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 p-4 mb-4 space-y-3"
          >
            <strong>Andamento da consulta</strong>
            <p>
              {{ journey.offers }} banco(s) com oferta ·
              {{ journey.processing }} em consulta ·
              {{ journey.pending }} aguardando autorização ·
              {{ journey.errors }} com erro
            </p>
            <p v-if="journeyIsActive">
              Após o aceite, a Talkvia continua nos bancos compatíveis para
              buscar margem e oferta. Mantenha esta tela aberta.
            </p>
            <Button
              v-else
              label="Acompanhar e continuar consulta"
              icon="i-lucide-play"
              @click="resumeJourney"
            />
            <div
              v-if="sharedAuthorization"
              class="border-t border-n-weak pt-3 space-y-2"
            >
              <strong>Um aceite Talkvia para os bancos compatíveis</strong>
              <p>
                O mesmo aceite válido será usado nas consultas deste cliente,
                para os bancos incluídos nos termos. C6 e Facta podem exigir
                autorização própria.
              </p>
              <p>
                Bancos aguardando:
                {{
                  sharedAuthorizationResults
                    .map(result => result.provider_label)
                    .join(', ')
                }}
              </p>
              <p v-if="sharedAuthorization.talkvia_authorization.ready">
                Aceite confirmado. Continuando a consulta para buscar ofertas.
              </p>
              <template v-else>
                <Button
                  :label="
                    sharedAuthorization.authorization_link
                      ? 'Abrir aceite Talkvia'
                      : 'Gerar aceite Talkvia'
                  "
                  @click="openResultAction(sharedAuthorization)"
                />
                <Button
                  v-if="sharedAuthorization.authorization_link"
                  label="Copiar link único"
                  variant="outline"
                  @click="copyAuthorizationLink(sharedAuthorization)"
                />
              </template>
            </div>
          </div>

          <div class="result-list">
            <details
              v-for="result in selectedBatch.results"
              :key="result.id"
              class="result-card"
              :class="statusClass(result.status)"
            >
              <summary class="result-heading">
                <div class="result-bank">
                  <BankLogo :provider="result.provider" />
                  <div>
                    <strong>{{ result.provider_label }}</strong>
                    <span>{{
                      result.duration_ms == null
                        ? 'Consulta CLT'
                        : `Retorno em ${formatDuration(result.duration_ms)}`
                    }}</span>
                  </div>
                </div>
                <span
                  class="status-pill compact"
                  :class="statusClass(result.status)"
                >
                  {{
                    result.offers?.length
                      ? 'Ofertas disponíveis'
                      : normalizeStatus(result.status)
                  }}
                </span>
                <span class="result-expand" aria-hidden="true">
                  <span class="result-expand-label">Ver resultados</span>
                  <span class="result-collapse-label">Recolher</span>
                  <Icon
                    icon="i-lucide-chevron-down"
                    class="size-4 result-chevron"
                  />
                </span>
              </summary>
              <div class="result-content">
                <div class="result-status-row">
                  <span
                    v-if="
                      result.status === 'success' &&
                      result.margin !== null &&
                      result.margin !== undefined
                    "
                    class="margin-value"
                  >
                    Margem disponível:
                    <strong>{{ formatCurrency(result.margin) }}</strong>
                  </span>
                </div>
                <p
                  v-if="
                    continuationErrors[result.id] && !isRejectedResult(result)
                  "
                  class="result-reason"
                  role="alert"
                >
                  <strong>Não foi possível continuar:</strong>
                  {{ continuationErrors[result.id] }}
                </p>
                <p v-if="isRejectedResult(result)" class="result-reason">
                  <strong>Motivo:</strong> {{ resultReason(result) }}
                </p>
                <p
                  v-else-if="
                    result.talkvia_authorization &&
                    result.status === 'authorization_required' &&
                    !continuationErrors[result.id]
                  "
                >
                  {{
                    result.talkvia_authorization.ready
                      ? 'Aceite confirmado. Pronto para buscar ofertas.'
                      : 'Utilize o aceite único na área acima.'
                  }}
                </p>
                <p v-else-if="result.offers?.length">
                  Compare {{ result.offers.length }} opções retornadas pelo
                  banco. A simulação não confirma a contratação de uma proposta.
                </p>
                <p v-else>
                  {{
                    result.error_detail ||
                    result.error_message ||
                    (result.response_summary === 'Status: operations_found'
                      ? 'Consulta concluída. Veja as operações retornadas em Detalhes.'
                      : result.response_summary) ||
                    'Aguardando processamento.'
                  }}
                </p>
                <BankOffers
                  v-if="result.offers?.length"
                  :key="result.id"
                  :offers="result.offers"
                />
                <p
                  v-if="
                    result.next_action?.available === false &&
                    result.next_action?.type === 'token'
                  "
                  class="action-unavailable"
                >
                  A validação segura de token ainda não está configurada no
                  servidor.
                </p>
                <p
                  v-if="
                    result.next_action?.available === false &&
                    result.next_action?.type === 'authorization'
                  "
                  class="action-unavailable"
                >
                  {{
                    result.next_action.unavailable_reason ||
                    'A integração não disponibilizou uma operação para gerar esta autorização.'
                  }}
                </p>
              </div>
              <footer class="result-footer">
                <span class="result-finished">
                  <Icon icon="i-lucide-clock-3" class="size-3.5 shrink-0" />
                  {{
                    result.finished_at
                      ? `Atualizado em ${formatDate(result.finished_at)}`
                      : result.status === 'awaiting'
                        ? 'Aguardando início'
                        : 'Consulta em andamento'
                  }}
                </span>
                <div class="result-actions">
                  <Button
                    v-if="
                      result.next_action &&
                      result.next_action.available !== false &&
                      (!result.talkvia_authorization ||
                        result.next_action.type !== 'authorization' ||
                        continuationErrors[result.id])
                    "
                    type="button"
                    :icon="
                      result.next_action.type === 'authorization'
                        ? 'i-lucide-external-link'
                        : 'i-lucide-list-restart'
                    "
                    size="xs"
                    :label="
                      continuationErrors[result.id]
                        ? 'Tentar novamente'
                        : result.next_action.label
                    "
                    :disabled="isContinuingAutomatically || isSubmittingAction"
                    @click="openResultAction(result)"
                  />
                  <Button
                    v-if="
                      result.authorization_link && !result.talkvia_authorization
                    "
                    type="button"
                    icon="i-lucide-copy"
                    color="slate"
                    variant="outline"
                    size="xs"
                    label="Copiar link"
                    :is-loading="copyingAuthorizationResultId === result.id"
                    :disabled="Boolean(copyingAuthorizationResultId)"
                    @click="copyAuthorizationLink(result)"
                  />
                  <Button
                    v-if="canRetryResult(result)"
                    type="button"
                    icon="i-lucide-refresh-cw"
                    color="slate"
                    variant="outline"
                    size="xs"
                    label="Reconsultar"
                    :is-loading="retryingResultId === result.id"
                    :disabled="Boolean(retryingResultId)"
                    @click="retryResult(result)"
                  />
                  <Button
                    class="result-details"
                    type="button"
                    icon="i-lucide-file-search"
                    color="slate"
                    variant="ghost"
                    size="xs"
                    label="Detalhes"
                    @click="openResultDetails(result)"
                  />
                </div>
              </footer>
            </details>
          </div>
        </template>
        <p v-else class="empty">
          Selecione ou crie uma consulta para acompanhar o retorno.
        </p>
      </section>
    </section>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      title="Excluir consulta?"
      :description="`A consulta ${pendingDeleteBatch?.cpf || ''} e todos os retornos dos bancos serão removidos permanentemente.`"
      confirm-button-label="Excluir consulta"
      cancel-button-label="Cancelar"
      :is-loading="Boolean(deletingBatchId)"
      @confirm="deleteBatch"
      @close="pendingDeleteBatch = null"
    />

    <Dialog
      ref="deleteCpfDialogRef"
      type="alert"
      title="Excluir todas as consultas deste CPF?"
      :description="`${pendingDeleteCpfGroup?.batches?.length || 0} consulta(s) de ${pendingDeleteCpfGroup?.cpf || ''} e todos os retornos dos bancos serão removidos permanentemente.`"
      confirm-button-label="Excluir todas"
      cancel-button-label="Cancelar"
      :is-loading="Boolean(deletingCpfKey)"
      @confirm="deleteCpfGroup"
      @close="pendingDeleteCpfGroup = null"
    />

    <Dialog
      ref="feedbackDialogRef"
      :title="feedback.title"
      :description="feedback.message"
      confirm-button-label="Fechar"
      :show-cancel-button="false"
      @confirm="feedbackDialogRef?.close()"
    />

    <div
      v-if="actionResult"
      class="modal-backdrop"
      @click.self="closeActionModal"
    >
      <section class="action-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <p class="eyebrow">Continuar consulta</p>
            <h2 class="flex items-center gap-3">
              <BankLogo :provider="actionResult.provider" />{{
                actionResult.provider_label
              }}
            </h2>
            <p>{{ actionModalDescription }}</p>
          </div>
          <Button
            class="icon-action"
            type="button"
            icon="i-lucide-x"
            color="slate"
            variant="ghost"
            size="sm"
            title="Fechar"
            @click="closeActionModal"
          />
        </header>
        <div class="action-fields">
          <p
            v-if="actionResult.next_action?.type === 'authorization'"
            class="secure-token-note"
          >
            O consentimento será aberto em uma página separada pelo link seguro
            retornado pelo próprio banco. A Talkvia não registra o aceite no
            lugar do cliente.
          </p>
          <div v-for="field in actionFields" :key="field" class="query-field">
            <label :for="`continuation-${field}`">
              {{ actionFieldLabels[field] || field }}
            </label>
            <Select
              v-if="field === 'gender'"
              :id="`continuation-${field}`"
              v-model="actionForm[field]"
              :options="GENDER_OPTIONS.filter(option => option.value)"
            />
            <Select
              v-else-if="field === 'banking_provider'"
              :id="`continuation-${field}`"
              v-model="actionForm[field]"
              :options="[
                { value: 'UY3', label: 'UY3' },
                { value: 'CELCOIN', label: 'Celcoin' },
              ]"
            />
            <Input
              v-else
              :id="`continuation-${field}`"
              v-model="actionForm[field]"
              :type="actionFieldType(field)"
              :inputmode="field === 'phone' ? 'tel' : undefined"
              :autocomplete="field === 'token' ? 'one-time-code' : 'off'"
            />
          </div>
          <p
            v-if="actionResult.next_action?.available === false"
            class="secure-token-note"
          >
            {{ actionUnavailableMessage }}
          </p>
        </div>
        <footer>
          <Button
            type="button"
            color="slate"
            variant="ghost"
            label="Cancelar"
            :disabled="isSubmittingAction"
            @click="closeActionModal"
          />
          <Button
            type="button"
            :label="actionSubmitLabel"
            :icon="
              actionResult.next_action?.type === 'authorization'
                ? 'i-lucide-external-link'
                : 'i-lucide-arrow-right'
            "
            :disabled="!actionCanSubmit"
            :is-loading="isSubmittingAction"
            @click="submitResultAction"
          />
        </footer>
      </section>
    </div>

    <div
      v-if="selectedLog"
      class="modal-backdrop"
      @click.self="closeResultDetails"
    >
      <section class="log-modal">
        <header>
          <div>
            <p class="eyebrow">Resultado da consulta</p>
            <h2 class="flex items-center gap-3">
              <BankLogo :provider="selectedLog.provider" />{{
                selectedLog.provider_label
              }}
            </h2>
            <p>Veja abaixo as informações principais retornadas pelo banco.</p>
          </div>
          <Button
            class="icon-action"
            type="button"
            icon="i-lucide-x"
            color="slate"
            variant="ghost"
            size="sm"
            title="Fechar"
            @click="closeResultDetails"
          />
        </header>
        <div class="log-summary">
          <div>
            <span>Status</span>
            <strong>{{ normalizeStatus(selectedLog.status) }}</strong>
          </div>
          <div>
            <span>Tempo de resposta</span>
            <strong>{{ formatDuration(selectedLog.duration_ms) }}</strong>
          </div>
          <div class="log-outcome" :class="statusClass(selectedLog.status)">
            <span>Resultado</span>
            <strong>{{ selectedLogOutcome }}</strong>
          </div>
        </div>
        <div class="friendly-log">
          <section v-if="friendlyLogMessages.length" class="bank-messages">
            <div class="friendly-section-heading">
              <Icon icon="i-lucide-info" class="size-5" />
              <div>
                <strong>Informações do banco</strong>
                <span>Avisos importantes sobre este resultado.</span>
              </div>
            </div>
            <p v-for="message in friendlyLogMessages" :key="message">
              {{ message }}
            </p>
          </section>

          <section v-if="selectedLog.offers?.length" class="friendly-section">
            <div class="friendly-section-heading">
              <Icon icon="i-lucide-badge-dollar-sign" class="size-5" />
              <div>
                <strong>Ofertas disponíveis</strong>
                <span>
                  Condições retornadas pelo banco. A simulação não confirma a
                  contratação de uma proposta.
                </span>
              </div>
            </div>
            <BankOffers
              :key="selectedLog.id"
              class="p-4"
              :offers="selectedLog.offers"
            />
          </section>

          <section v-if="friendlyLogFacts.length" class="friendly-section">
            <div class="friendly-section-heading">
              <Icon icon="i-lucide-list-checks" class="size-5" />
              <div>
                <strong>Dados da consulta</strong>
                <span>Principais informações confirmadas pelo banco.</span>
              </div>
            </div>
            <dl class="friendly-facts">
              <div
                v-for="fact in friendlyLogFacts"
                :key="`${fact.label}-${fact.value}`"
              >
                <dt>{{ fact.label }}</dt>
                <dd>{{ fact.value }}</dd>
              </div>
            </dl>
          </section>

          <section
            v-if="!friendlyLogMessages.length && !friendlyLogFacts.length"
            class="friendly-empty"
          >
            <Icon icon="i-lucide-file-check-2" class="size-7" />
            <strong>{{ selectedLogOutcome }}</strong>
            <span>O banco não enviou outras informações para exibição.</span>
          </section>

          <section class="technical-details">
            <button
              type="button"
              class="technical-toggle"
              @click="showTechnicalDetails = !showTechnicalDetails"
            >
              <span>
                <Icon icon="i-lucide-code-2" class="size-4" />
                Dados técnicos para suporte
              </span>
              <Icon
                :icon="
                  showTechnicalDetails
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
                class="size-4"
              />
            </button>
            <pre v-if="showTechnicalDetails">{{
              formatJson(selectedLog.log || selectedLog.raw_response)
            }}</pre>
          </section>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.bank-query-page {
  @apply flex h-full min-h-0 w-full flex-col gap-3.5 overflow-y-auto overflow-x-hidden bg-n-background p-6 text-n-slate-12;
}
.bank-query-page.is-embedded {
  @apply h-auto overflow-visible bg-transparent p-0;
}
.bank-query-page.is-embedded .recent-card {
  @apply static max-h-none overflow-visible;
}
.bank-query-page.is-embedded .query-grid {
  grid-template-columns: minmax(0, 1fr);
}
.bank-hero {
  @apply mx-auto flex w-full max-w-[1280px] items-start justify-between gap-5 pb-2;
}
.bank-hero h1,
.recent-card h2,
.detail-card h2,
.log-modal h2 {
  @apply m-0;
}
.bank-hero h1 {
  @apply text-2xl leading-tight;
}
.bank-hero p {
  @apply mt-1.5 max-w-[760px] text-sm text-n-slate-11;
}
.eyebrow {
  @apply mb-1.5 mt-0 text-xs font-bold text-n-slate-10;
}
.summary-grid {
  @apply mx-auto grid w-full max-w-[1280px] grid-cols-3 gap-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-2;
}
.summary-card {
  @apply flex items-center gap-3.5 border-r border-n-weak bg-transparent px-5 py-4;
}
.summary-card:last-child {
  @apply border-r-0;
}
.summary-icon {
  @apply grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak bg-n-solid-1 text-n-brand;
}
.summary-icon :deep(span) {
  @apply size-5;
}
.summary-card strong {
  @apply block text-[26px] leading-none;
}
.summary-card small {
  @apply mt-1 block text-xs text-n-slate-10;
}
.query-card,
.recent-card,
.detail-card {
  @apply rounded-xl border border-n-weak bg-n-solid-2;
}
.query-card p,
.detail-header p,
.empty,
small,
.recent-header p,
.batch-main span,
.log-modal p {
  @apply text-n-slate-10;
}
.secondary-query-card {
  @apply mx-auto w-full max-w-[1280px] p-5;
}
.customer-details p,
.query-helper {
  @apply m-0 mt-1 text-xs text-n-slate-10;
}
.no-banks-state {
  @apply flex items-center gap-4 rounded-xl border border-dashed border-n-weak bg-n-alpha-1 p-5;
}
.no-banks-state__icon {
  @apply grid size-11 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand;
}
.no-banks-state > div {
  @apply min-w-0 flex-1;
}
.no-banks-state strong {
  @apply block text-sm text-n-slate-12;
}
.no-banks-state p {
  @apply mt-1 text-xs text-n-slate-10;
}
.bank-selection-header {
  @apply flex items-center justify-between gap-4;
}
.section-disclosure {
  @apply flex min-w-0 flex-1 items-center gap-3 rounded-lg border-0 bg-transparent p-0 text-left text-n-slate-12;
}
.section-disclosure > :deep(span:first-child),
.section-disclosure > :deep(span:last-child) {
  @apply shrink-0 text-n-brand;
}
.section-disclosure-copy {
  @apply min-w-0 flex-1;
}
.section-disclosure-copy strong,
.section-disclosure-copy small {
  @apply block;
}
.section-disclosure-copy strong {
  @apply text-sm;
}
.section-disclosure-copy small {
  @apply mt-0.5 text-xs font-normal text-n-slate-10;
}
.selection-actions {
  @apply flex items-center gap-3;
}
.selection-actions button {
  @apply text-xs font-medium text-n-brand hover:underline;
}
.bank-selection-grid {
  @apply mt-3 grid grid-flow-col auto-cols-[minmax(172px,1fr)] grid-rows-1 gap-2 overflow-x-auto pb-1;
}
.bank-option {
  @apply min-w-0 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 text-left transition-all hover:border-n-slate-7 hover:bg-n-alpha-1;
}
.bank-option.is-selected {
  @apply border-n-brand bg-n-blue-3 ring-1 ring-n-brand/20 dark:border-n-blue-8 dark:bg-n-solid-3 dark:ring-n-blue-8/30;
}
.bank-option-toggle {
  @apply flex w-full min-w-0 items-center gap-2 p-2 text-left;
}
.bank-only-action {
  @apply block w-full truncate border-t border-n-weak px-2 py-1.5 text-left text-[10px] font-medium leading-4 text-n-brand hover:bg-n-alpha-2;
}
.bank-monogram {
  @apply grid size-8 shrink-0 place-content-center rounded-md bg-n-alpha-2 text-[10px] font-bold text-n-slate-11;
}
.bank-monogram.small {
  @apply size-9;
}
.bank-option.is-selected .bank-monogram {
  @apply bg-n-brand text-white;
}
.bank-option-copy {
  @apply min-w-0 flex-1;
}
.bank-option-copy strong,
.bank-option-copy small {
  @apply block truncate;
}
.bank-option-copy strong {
  @apply text-xs text-n-slate-12 dark:text-n-slate-12;
}
.bank-option-copy small {
  @apply mt-0.5 text-[10px] leading-4 text-n-slate-10 dark:text-n-slate-11;
}
.bank-check {
  @apply shrink-0 text-n-slate-8;
}
.bank-option.is-selected .bank-check {
  @apply text-n-brand;
}
.bank-empty-state {
  @apply mt-3 flex items-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 p-4 text-sm text-n-slate-10;
}
.customer-details {
  @apply mt-4 rounded-lg border border-n-weak bg-n-alpha-1 p-4;
}
.customer-details-heading {
  @apply flex w-full items-start gap-3;
}
.customer-grid {
  @apply mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3;
}
.select-field {
  @apply flex flex-col gap-1.5;
}
.query-action-row {
  @apply mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3;
}
.query-field {
  @apply grid gap-2;
}
.input-group {
  @apply flex w-full items-stretch;
}
label {
  @apply mb-0 block text-xs font-bold leading-5;
}
.query-submit {
  @apply min-w-44;
}
.query-actions {
  @apply mt-7 flex items-center gap-2;
}
.contact-lookup-message {
  @apply font-medium text-n-teal-11;
}
button:disabled {
  @apply cursor-not-allowed opacity-55;
}
.query-grid {
  @apply mx-auto grid w-full max-w-[1280px] grid-cols-[380px_minmax(0,1fr)] items-start gap-3.5;
}
.recent-card,
.detail-card {
  @apply p-4;
}
.recent-card {
  @apply sticky top-0 flex max-h-[calc(100dvh-2rem)] flex-col gap-2.5 overflow-y-auto;
}
.recent-header {
  @apply flex justify-between gap-3;
}
.batch-card {
  @apply relative grid gap-2 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 py-3.5 pl-4 pr-[46px] text-sm leading-5 transition-colors;
}
.cpf-group-card {
  @apply shrink-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 transition-colors;
}
.cpf-group-card.is-selected {
  @apply border-n-brand ring-1 ring-n-brand/20;
}
.cpf-group-header {
  @apply flex flex-wrap items-center gap-2 border-b border-n-weak bg-n-alpha-1 px-3 py-2.5;
}
.cpf-group-title {
  @apply flex min-w-0 flex-1 items-center gap-2.5 border-0 bg-transparent p-0 text-left;
}
.cpf-group-title > span:last-child {
  @apply min-w-0;
}
.cpf-group-title strong,
.cpf-group-title small {
  @apply block truncate;
}
.cpf-group-title strong {
  @apply text-sm text-n-slate-12;
}
.cpf-group-title small {
  @apply mt-0.5 text-[11px] text-n-slate-10;
}
.cpf-group-icon {
  @apply grid size-8 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand;
}
.cpf-group-actions {
  @apply flex shrink-0 items-center gap-1;
}
.cpf-group-compact {
  @apply inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-[11px] font-semibold text-n-slate-11 transition hover:border-n-brand/40 hover:bg-n-brand/5 hover:text-n-brand;
}
.cpf-group-attempts {
  @apply grid gap-1 p-1.5;
}
.batch-attempt {
  @apply grid grid-cols-[minmax(0,1fr)_auto] items-center rounded-lg border border-transparent pl-2 transition-colors hover:bg-n-alpha-1;
}
.batch-attempt.is-selected {
  @apply border-n-brand/30 bg-n-brand/5;
}
.batch-attempt-main {
  @apply flex min-w-0 items-center justify-between gap-2 border-0 bg-transparent py-2 text-left;
}
.batch-attempt-main > span:first-child {
  @apply min-w-0;
}
.batch-attempt-main strong,
.batch-attempt-main small {
  @apply block truncate;
}
.batch-attempt-main strong {
  @apply text-xs font-semibold text-n-slate-12;
}
.batch-attempt-main small {
  @apply mt-0.5 text-[11px] text-n-slate-10;
}
.batch-attempt-delete {
  @apply mx-1 !size-7 !p-0;
}
.batch-card::before {
  @apply absolute inset-y-0 left-0 w-1 bg-n-slate-7 content-[''];
}
.batch-card.is-selected {
  @apply bg-n-alpha-1 ring-1;
}
.batch-card.is-selected.is-success {
  @apply border-n-teal-8 ring-n-teal-8/30;
}
.batch-card.is-selected.is-processing {
  @apply border-n-blue-8 ring-n-blue-8/30;
}
.batch-card.is-selected.is-waiting {
  @apply border-n-slate-8 ring-n-slate-8/30;
}
.batch-card.is-selected.is-warning {
  @apply border-n-amber-8 ring-n-amber-8/30;
}
.batch-card.is-selected.is-error {
  @apply border-n-ruby-8 ring-n-ruby-8/30;
}
.batch-main {
  @apply grid cursor-pointer gap-1 border-0 bg-transparent p-0 text-left text-sm;
  color: inherit;
}
.batch-topline strong {
  @apply text-sm leading-5;
}
.batch-main span {
  @apply text-xs leading-5;
}
.batch-topline {
  @apply flex items-center justify-between gap-3;
}
.status-pill.is-success {
  @apply bg-n-teal-3 text-n-teal-11;
}
.status-pill.is-processing {
  @apply bg-n-blue-3 text-n-blue-11;
}
.status-pill.is-waiting {
  @apply bg-n-alpha-2 text-n-slate-11;
}
.status-pill.is-warning {
  @apply bg-n-amber-3 text-n-amber-11;
}
.status-pill.is-error {
  @apply bg-n-ruby-3 text-n-ruby-11;
}
.batch-card.is-success::before {
  @apply bg-n-teal-9;
}
.batch-card.is-processing::before {
  @apply bg-n-blue-9;
}
.batch-card.is-warning::before {
  @apply bg-n-amber-9;
}
.batch-card.is-error::before {
  @apply bg-n-ruby-9;
}
.batch-metrics {
  @apply flex flex-wrap gap-1.5;
}
.batch-metrics span {
  @apply rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium;
}
.batch-metrics .metric-success {
  @apply bg-n-teal-3 text-n-teal-11;
}
.batch-metrics .metric-error {
  @apply bg-n-ruby-3 text-n-ruby-11;
}
.batch-period {
  @apply flex items-center gap-1.5;
}
.detail-header {
  @apply grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4;
}
.status-pill {
  @apply inline-flex justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs leading-[1.125rem];
}
.status-pill.compact {
  @apply px-2 py-1;
}
.cpf-line {
  @apply flex flex-wrap items-center gap-1.5;
}
.icon-inline {
  @apply !size-6 !rounded-full;
}
.icon-action {
  @apply !size-8 !p-0;
}
.icon-action.danger {
  @apply absolute right-2.5 top-2.5;
}
.progress {
  @apply my-3.5 h-1.5 overflow-hidden rounded-full bg-n-alpha-2;
}
.progress span {
  @apply block h-full bg-n-blue-9;
}
.result-list {
  @apply grid gap-4;
}
.result-card {
  @apply min-w-0 rounded-xl border border-n-weak bg-n-solid-1 shadow-sm;
}
.result-card.is-processing {
  @apply border-n-blue-7;
}
.result-card.is-warning {
  @apply border-n-amber-7;
}
.result-card.is-error {
  @apply border-n-ruby-7;
}
.result-heading {
  @apply flex flex-wrap items-center gap-3 p-4 cursor-pointer rounded-xl;
  list-style: none;
}
.result-heading::-webkit-details-marker {
  display: none;
}
.result-heading:hover {
  @apply bg-n-alpha-1;
}
.result-heading:focus-visible {
  @apply outline outline-2 outline-offset-2 outline-n-brand;
}
.result-heading > .status-pill {
  margin-left: auto;
}
.result-expand {
  @apply inline-flex items-center gap-2 text-xs text-n-slate-11;
}
.result-collapse-label {
  display: none;
}
.result-card[open] > .result-heading .result-expand-label {
  display: none;
}
.result-card[open] > .result-heading .result-collapse-label {
  display: inline;
}
.result-card[open] > .result-heading .result-chevron {
  transform: rotate(180deg);
}
.result-bank {
  @apply flex min-w-0 items-center gap-3;
}
.result-bank-icon {
  @apply flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11;
}
.result-bank strong {
  @apply block text-sm font-semibold leading-5 text-n-slate-12;
}
.result-bank div > span {
  @apply block mt-0.5 text-xs text-n-slate-10;
}
.result-content {
  @apply min-w-0 px-4 pb-4 pt-3;
}
.result-status-row {
  @apply flex flex-wrap items-center gap-2 empty:hidden;
}
.margin-value {
  @apply mb-2 text-sm text-n-slate-11;
}
.result-content p {
  @apply m-0 break-words text-sm leading-6 text-n-slate-11;
  overflow-wrap: anywhere;
}
.result-content .result-reason {
  @apply rounded-lg bg-n-ruby-3 px-3 py-2 text-n-ruby-11;
}
.result-content .result-reason + p {
  @apply mt-2;
}
.result-footer {
  @apply flex flex-wrap items-center justify-between gap-3 border-t border-n-weak px-4 py-3;
}
.result-finished {
  @apply flex items-center gap-1.5 text-xs text-n-slate-10;
}
.result-details {
  @apply shrink-0;
}
.result-actions {
  @apply flex flex-wrap items-center gap-2;
}
.offers-list {
  @apply mt-3 grid gap-3;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
}
.offer-card {
  @apply min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-3;
}
.offer-heading {
  @apply mb-3 flex flex-wrap items-center justify-between gap-2 text-xs font-medium text-n-slate-11;
}
.offer-heading > span:last-child:not(:first-child) {
  @apply rounded bg-n-alpha-2 px-2 py-0.5;
}
.offer-label {
  @apply block text-xs text-n-slate-10;
}
.offer-amount {
  @apply mt-1 block text-xl font-semibold tabular-nums text-n-teal-11;
}
.offer-terms {
  @apply mb-0 mt-3 space-y-2 border-t border-n-weak pt-3;
}
.offer-terms > div {
  @apply flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs;
}
.offer-terms dt {
  @apply text-n-slate-10;
}
.offer-terms dd {
  @apply m-0 break-words font-medium tabular-nums text-n-slate-12;
}
.action-unavailable,
.secure-token-note {
  @apply rounded-lg border border-n-amber-7 bg-n-amber-3 p-3 text-xs text-n-amber-11;
}

.modal-backdrop {
  @apply fixed inset-0 z-50 grid place-items-center bg-n-alpha-black2 p-6 backdrop-blur;
}
.log-modal {
  @apply flex h-[min(88dvh,860px)] w-[min(920px,100%)] flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-2xl;
}
.action-modal {
  @apply w-[min(560px,100%)] overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-2xl;
}
.action-modal header {
  @apply flex items-start justify-between gap-4 border-b border-n-weak px-5 py-4;
}
.action-modal header p:not(.eyebrow) {
  @apply mb-0 mt-1 text-sm text-n-slate-10;
}
.action-fields {
  @apply grid gap-4 p-5;
}
.action-modal footer {
  @apply flex justify-end gap-2 border-t border-n-weak px-5 py-4;
}
.log-modal header {
  @apply flex shrink-0 items-center justify-between gap-4 border-b border-n-weak px-5 py-4;
}
.log-modal header p:not(.eyebrow) {
  @apply mb-0 mt-1 text-sm text-n-slate-10;
}
.log-summary {
  @apply grid shrink-0 grid-cols-[160px_180px_minmax(0,1fr)] gap-3 border-b border-n-weak bg-n-solid-1 px-5 py-4;
}
.log-summary > div {
  @apply rounded-lg border border-n-weak bg-n-solid-2 p-3;
}
.log-summary span,
.log-summary strong {
  @apply block;
}
.log-summary span {
  @apply text-xs text-n-slate-10;
}
.log-summary strong {
  @apply mt-1 break-words text-sm text-n-slate-12;
}
.log-outcome.is-success strong {
  @apply text-n-teal-11;
}
.log-outcome.is-warning strong {
  @apply text-n-amber-11;
}
.log-outcome.is-error strong {
  @apply text-n-ruby-11;
}
.friendly-log {
  @apply min-h-0 flex-1 space-y-4 overflow-y-auto bg-n-solid-1 p-5;
}
.friendly-section,
.bank-messages,
.technical-details {
  @apply overflow-hidden rounded-xl border border-n-weak bg-n-solid-2;
}
.friendly-section-heading {
  @apply flex items-center gap-3 border-b border-n-weak px-4 py-3;
}
.friendly-section-heading > :deep(span) {
  @apply shrink-0 text-n-brand;
}
.friendly-section-heading strong,
.friendly-section-heading span {
  @apply block;
}
.friendly-section-heading strong {
  @apply text-sm text-n-slate-12;
}
.friendly-section-heading span {
  @apply mt-0.5 text-xs text-n-slate-10;
}
.bank-messages {
  @apply border-n-amber-7 bg-n-amber-3;
}
.bank-messages .friendly-section-heading {
  @apply border-n-amber-7;
}
.bank-messages .friendly-section-heading > :deep(span) {
  @apply text-n-amber-11;
}
.bank-messages > p {
  @apply m-0 border-b border-n-amber-7/40 px-4 py-3 text-sm leading-6 text-n-amber-12 last:border-b-0;
}
.friendly-facts {
  @apply grid grid-cols-2 gap-px bg-n-weak;
}
.friendly-facts > div {
  @apply min-w-0 bg-n-solid-2 px-4 py-3;
}
.friendly-facts dt {
  @apply text-xs text-n-slate-10;
}
.friendly-facts dd {
  @apply mb-0 mt-1 break-words text-sm font-semibold text-n-slate-12;
}
.log-offers-grid {
  @apply grid grid-cols-2 gap-3 p-4;
}
.log-offer-card {
  @apply rounded-xl border border-n-teal-7 bg-n-teal-3 p-4;
}
.log-offer-card > span,
.log-offer-card small {
  @apply block text-xs text-n-slate-10;
}
.log-offer-card > strong {
  @apply my-1 block text-xl text-n-teal-11;
}
.log-offer-card > div {
  @apply flex flex-wrap gap-x-3 gap-y-1;
}
.friendly-empty {
  @apply grid place-items-center gap-1 rounded-xl border border-dashed border-n-weak bg-n-solid-2 p-8 text-center;
}
.friendly-empty > :deep(span) {
  @apply mb-2 text-n-teal-10;
}
.friendly-empty strong {
  @apply text-sm;
}
.friendly-empty > span {
  @apply text-xs text-n-slate-10;
}
.technical-toggle {
  @apply flex w-full items-center justify-between gap-4 bg-transparent px-4 py-3 text-left text-xs font-medium text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12;
}
.technical-toggle > span {
  @apply flex items-center gap-2;
}
.technical-details pre {
  @apply m-0 max-h-80 overflow-auto border-t border-n-weak bg-n-solid-1 p-4 text-xs leading-6 text-n-slate-12 whitespace-pre-wrap;
}
@media (max-width: 1180px) {
  .query-grid {
    @apply grid-cols-1;
  }
}
@media (max-width: 760px) {
  .bank-query-page {
    @apply p-4;
  }
  .bank-hero {
    @apply flex-col;
  }
  .query-card-header,
  .bank-selection-header {
    @apply items-stretch;
  }
  .customer-grid {
    @apply grid-cols-1;
  }
  .result-details {
    @apply flex-1;
  }
  .result-actions {
    @apply w-full;
  }
  .result-actions > :deep(*) {
    @apply flex-1;
  }
  .log-summary {
    @apply grid-cols-1;
  }
  .friendly-facts,
  .log-offers-grid {
    @apply grid-cols-1;
  }
  .summary-grid {
    @apply grid-cols-1;
  }
  .summary-card {
    @apply border-r-0 border-b;
  }
}
@media (max-width: 640px) {
  .query-card-header,
  .bank-selection-header,
  .query-action-row {
    @apply flex-col gap-2;
  }
  .query-action-row {
    @apply flex;
  }
  .query-field {
    @apply w-full;
  }
  .query-submit {
    @apply mt-0 w-full rounded-lg;
  }
  .query-actions {
    @apply mt-0 w-full flex-col;
  }
}
</style>
